import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ClipboardList, ArrowRight, ArrowLeft, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const FEATURE_OPTIONS = [
  "Parent Assessment", "AI Parent Assistant", "Curriculum Explorer",
  "Future Careers", "Marketplace / Shop", "Communities",
  "Tutors & Mentors", "Activities & Schedules", "Learning for All impact",
];
const IMPROVE_OPTIONS = [
  "More curricula", "Faster page loads", "Local language support",
  "Better mobile UX", "More products in shop", "Live tutor booking",
  "Group forums", "Video content", "Progress tracking for my child",
];

const Q = [
  { key: "ux_rating", label: "How do you rate the look & feel of the website?", type: "rating" },
  { key: "ease_of_navigation", label: "How easy was it to move around the site?", type: "choice", options: ["Easy", "Medium", "Hard"] },
  { key: "information_helpful", label: "Was the information on the site helpful?", type: "choice", options: ["Yes", "Somewhat", "No"] },
  { key: "satisfaction", label: "How satisfied are you overall?", type: "rating" },
  { key: "would_recommend", label: "Would you recommend FuturePath to another parent?", type: "choice", options: ["Yes", "Maybe", "No"] },
  { key: "favourite_features", label: "Which features did you love most?", type: "multi", options: FEATURE_OPTIONS },
  { key: "improvement_areas", label: "What should we improve next?", type: "multi", options: IMPROVE_OPTIONS },
  { key: "suggestions", label: "Anything else you'd like to tell us?", type: "text" },
  { key: "email", label: "Your email (optional, only if you'd like a reply)", type: "email" },
];

export default function Survey() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    ux_rating: 0, satisfaction: 0,
    favourite_features: [], improvement_areas: [],
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const cur = Q[step];
  const total = Q.length;
  const progress = Math.round(((step) / (total - 1)) * 100);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleMulti = (k, v) => {
    const arr = data[k] || [];
    set(k, arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  };
  const canNext = () => {
    if (!cur) return true;
    if (cur.type === "rating") return data[cur.key] > 0;
    if (cur.type === "choice") return !!data[cur.key];
    if (cur.type === "multi") return (data[cur.key] || []).length > 0;
    return true; // text/email optional
  };

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/survey", {
        ux_rating: data.ux_rating,
        ease_of_navigation: data.ease_of_navigation,
        information_helpful: data.information_helpful,
        satisfaction: data.satisfaction,
        would_recommend: data.would_recommend,
        favourite_features: data.favourite_features || [],
        improvement_areas: data.improvement_areas || [],
        suggestions: data.suggestions || null,
        email: data.email || null,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not submit survey");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center">
        <div className="w-20 h-20 rounded-3xl bg-brand-green-soft text-brand-green mx-auto mb-6 flex items-center justify-center"><CheckCircle2 size={40} /></div>
        <h1 className="text-4xl font-semibold text-brand-blue">Thank you.</h1>
        <p className="mt-3 text-brand-muted">Your voice will directly shape what we build next. That's a promise.</p>
        <div className="mt-8 flex flex-col gap-3">
          <Link to="/" className="btn-primary">Back to home</Link>
          <Link to="/dashboard" className="btn-outline">Go to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16 min-h-[80vh]">
      <div className="mb-10">
        <div className="flex justify-between text-xs text-brand-muted mb-2">
          <span>Question {step + 1} of {total}</span><span>{progress}%</span>
        </div>
        <div className="h-2 bg-brand-divider rounded-full overflow-hidden">
          <motion.div className="h-full bg-brand-green" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
          {step === 0 && (
            <div className="mb-6 text-center">
              <ClipboardList size={40} className="text-brand-green mx-auto mb-3" />
              <div className="overline mb-2">Website Feedback Survey</div>
              <p className="text-sm text-brand-muted">Nine quick questions — takes 2 minutes. Your answers are anonymous unless you leave an email.</p>
            </div>
          )}
          <h2 className="text-3xl font-semibold text-brand-blue">{cur.label}</h2>

          {cur.type === "rating" && (
            <div className="mt-6 flex gap-3">
              {[1,2,3,4,5].map((n) => (
                <button key={n} data-testid={`survey-${cur.key}-${n}`} onClick={() => set(cur.key, n)}
                  className={`w-16 h-16 rounded-2xl border-2 font-heading font-bold text-xl transition-all ${
                    data[cur.key] === n ? "border-brand-green bg-brand-green-soft text-brand-blue" : "border-brand-border bg-white text-brand-muted hover:border-brand-blue"
                  }`}>
                  {n}
                </button>
              ))}
            </div>
          )}

          {cur.type === "choice" && (
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              {cur.options.map((opt) => (
                <button key={opt} data-testid={`survey-${cur.key}-${opt.toLowerCase()}`}
                  onClick={() => { set(cur.key, opt); setTimeout(() => setStep((s) => Math.min(s+1, total-1)), 200); }}
                  className={`px-5 py-4 rounded-2xl border-2 transition-all ${
                    data[cur.key] === opt ? "border-brand-green bg-brand-green-soft text-brand-blue" : "border-brand-border bg-white text-brand-blue hover:border-brand-blue"
                  }`}>{opt}</button>
              ))}
            </div>
          )}

          {cur.type === "multi" && (
            <div className="mt-6 flex flex-wrap gap-2">
              {cur.options.map((opt) => {
                const on = (data[cur.key] || []).includes(opt);
                return (
                  <button key={opt} data-testid={`survey-${cur.key}-${opt.toLowerCase().replace(/\s+/g,'-')}`}
                    onClick={() => toggleMulti(cur.key, opt)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                      on ? "bg-brand-blue text-white border-brand-blue" : "bg-white text-brand-blue border-brand-border hover:border-brand-blue"
                    }`}>{opt}</button>
                );
              })}
            </div>
          )}

          {cur.type === "text" && (
            <textarea data-testid={`survey-${cur.key}`} rows={5} className="field mt-6 resize-none"
              placeholder="Optional — write anything you want us to hear..."
              value={data[cur.key] || ""} onChange={(e) => set(cur.key, e.target.value)} />
          )}

          {cur.type === "email" && (
            <input data-testid={`survey-${cur.key}`} type="email" className="field mt-6"
              placeholder="you@example.com — optional"
              value={data[cur.key] || ""} onChange={(e) => set(cur.key, e.target.value)} />
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-10 flex justify-between">
        <button data-testid="survey-back-btn" onClick={() => setStep((s) => Math.max(s-1, 0))} className="btn-ghost" disabled={step === 0}>
          <ArrowLeft size={16} /> Back
        </button>
        {step < total - 1 ? (
          <button data-testid="survey-next-btn" onClick={() => setStep((s) => Math.min(s+1, total-1))} disabled={!canNext()} className="btn-primary disabled:opacity-40">
            Continue <ArrowRight size={16} />
          </button>
        ) : (
          <button data-testid="survey-submit-btn" onClick={submit} disabled={loading} className="btn-primary disabled:opacity-60">
            {loading ? (<><Loader2 className="animate-spin" size={14} /> Submitting</>) : (<><Sparkles size={14} /> Submit</>)}
          </button>
        )}
      </div>
    </div>
  );
}
